import DisplayField from '@shared/components/displays/display-field/display-field.vue';
import TableMock from '@shared/components/mocks/table-mock/table-mock.vue';
import TableFooter from '@shared/components/tables/table-footer/table-footer.vue';
import TableHeader from '@shared/components/tables/table-header/table-header.vue';
import PaginationInterface from '@core/interfaces/pagination-interface';
import Vue from 'vue';
import { Component, Prop, PropSync, Watch } from 'vue-property-decorator';
import { get, replace } from 'lodash';
import Avatar from 'vue-avatar';
import Grid from '@shared/components/grid/grid.vue';
import GridMock from '@shared/components/mocks/grid-mock/grid-mock.vue';
import { AccessControlConfigInterface } from '@core/interfaces/access-control-config-interface';

@Component({
  name: 'bpc-entity-table',
  components: {
    'bpc-table-header': TableHeader,
    'bpc-table-footer': TableFooter,
    'bpc-table-mock': TableMock,
    'bpc-display-field': DisplayField,
    'bpc-grid': Grid,
    'bpc-grid-mock': GridMock,
    Avatar,
  },
})
export default class EntityTable extends Vue {
  @Prop(Array) readonly items: object[];
  @Prop(Array) readonly fields: object[];
  @Prop(String) readonly entity: string;
  @Prop({ type: Boolean, default: false }) readonly tableSortable: boolean;
  @Prop(Boolean) readonly loading: boolean;
  @Prop({ type: Boolean, default: false }) readonly checkedAll: boolean;
  @Prop({ type: Boolean, default: true }) readonly showTableHeader: boolean;
  @Prop({ type: Boolean, default: true }) readonly showTableTitlesRow: boolean;
  @Prop(String) readonly cardType: string;
  // @Prop(Object) readonly pagination: PaginationInterface;
  @PropSync('pagination', { type: Object }) syncedPagination!: PaginationInterface;
  @PropSync('search', { type: String }) syncedSearch!: string;
  @PropSync('orderDirection', { type: String }) syncedOrderDirection!: string;
  @PropSync('orderBy', { type: String }) syncedOrderBy!: string;
  @Prop(Boolean) readonly tableSorteable: boolean;
  @Prop({ type: String, default: 'table' }) readonly initialView: string;
  @Prop({ type: Boolean, default: false }) readonly disabled: boolean;
  @Prop({ type: Boolean, default: false }) readonly showActionButtonText: boolean;
  @Prop({ type: Boolean, default: true }) readonly withSearch: boolean;
  @Prop({ type: Boolean, default: true }) readonly showOrderBy: boolean;
  @Prop({ type: Boolean, default: true }) readonly showPagination: boolean;
  @Prop({ type: Boolean, default: false }) readonly noCheckBoxColumn: boolean;
  @Prop({
    type: Array,
    default() {
      return ['table'];
    },
  })
  readonly availableViews: string[];
  @Prop({
    type: [Array, Object],
    default() {
      return [];
    },
  })
  readonly gridCols: string[] | object;

  public isDropdownActive: boolean = false;

  public view = 'table';
  get sortableOptions() {
    return {
      sort: this.tableSortable,
      chosenClass: 'is-selected',
      handle: '.svg-inline--fa',
      onEnd: event => {
        const dragged_id = this.items[event.oldIndex]['id'];
        const replaced_id = this.items[event.newIndex]['id'];
        this.$emit('change-order', { dragged_id: dragged_id, replaced_id: replaced_id });
      },
    };
  }

  get gridViewAvailable(): boolean {
    if (!Array.isArray(this.availableViews)) {
      return false;
    }
    return this.availableViews.includes('grid');
  }

  get tableViewAvailable(): boolean {
    if (!Array.isArray(this.availableViews)) {
      return true; // This is the default case
    }
    return this.availableViews.includes('table');
  }

  get calendarViewAvailable(): boolean {
    if (!Array.isArray(this.availableViews)) {
      return true; // This is the default case
    }
    return this.availableViews.includes('calendar');
  }

  get fieldsFilterable(): object[] {
    return this.fields.reduce((fields: object[], field: Record<string, any>) => {
      if (!field.conditionalColumnHide && !field.conditionalColumnShow && !field.module) {
        fields.push(field);
      } else {
        if (!this.hideField(field)) {
          fields.push(field);
        }
      }
      return fields;
    }, []);
  }

  get fieldsToShow(): object[] {
    return this.fields.reduce((fields: object[], field: Record<string, any>) => {
      7;
      if (
        !field.conditionalColumnHide &&
        !field.conditionalColumnShow &&
        !field.module &&
        field.displayType !== 'hidden'
      ) {
        fields.push(field);
      } else {
        if (!this.hideField(field) && field.displayType !== 'hidden') {
          fields.push(field);
        }
      }
      return fields;
    }, []);
  }

  get hasTotalAmount(): boolean {
    const withTotalAmount = this.fieldsToShow.filter(field => field['withTotalAmount']);
    return !!withTotalAmount.length;
  }

  get cancelledFilter(): boolean {
    if (!this.items) {
      return false;
    }
    return this.items.every((val, i, arr) => val['status'] === 'cancelled');
  }

  get accessControlConfig(): AccessControlConfigInterface {
    return this.$store.getters['centerSettings/accessControlConfig'];
  }

  rowActions(actions: object[]) {
    return actions
      ? Object.values(actions).filter(
          action => !action['dropdown'] && action['show'] && action['activated'] && !this.disabled,
        )
      : [];
  }

  rowMoreActions(actions: object[]) {
    return actions
      ? Object.values(actions).filter(
          action => action['dropdown'] && action['show'] && action['activated'] && !this.disabled,
        )
      : [];
  }

  getTotalAmount(data) {
    if (!this.items) {
      return 0;
    }
    const fieldKey = data.field.key;
    const total = this.items.reduce((sum, item) => {
      if (!item['parent']) {
        let value = item[fieldKey];
        if (typeof value === 'string') {
          value = value.replace(',', '');
        }
        const parsedAsNumber = Number(String(value));
        const finalValue = !isNaN(parsedAsNumber) ? parsedAsNumber : 0;
        if (this.cancelledFilter || item['status'] !== 'cancelled') {
          return sum + finalValue;
        }
      }
      return sum;
    }, 0);

    return total.toFixed(2);
  }

  getTotalAmountFieldConfig(data) {
    const config = data.field;
    this.$delete(config, 'conditionalHide');
    this.$delete(config, 'conditionalShow');
    return config;
  }

  hideField(fieldConfig): boolean {
    let show = true;
    let hide = false;
    // console.log('FieldConfig', fieldConfig);
    const showIfFieldPath = get(fieldConfig, 'conditionalColumnShow.fieldPath', null);
    if (showIfFieldPath) {
      const showIfOperator = get(fieldConfig, 'conditionalColumnShow.operator', '=');
      const showIfValue = get(fieldConfig, 'conditionalColumnShow.value', null);
      const modelValue = get(this, showIfFieldPath);
      const formModel = get(fieldConfig, 'conditionalColumnShow.formModel');
      if (formModel) {
        const modelValue = get(this, `${formModel}.${showIfFieldPath}`, null);
        if (!this.compare(showIfOperator, modelValue, showIfValue)) {
          show = false;
        }
      } else {
        if (!this.compare(showIfOperator, modelValue, showIfValue)) {
          show = false;
        }
      }
    }

    const hideIfFieldPath = get(fieldConfig, 'conditionalColumnHide.fieldPath', null);
    if (hideIfFieldPath) {
      const hideIfOperator = get(fieldConfig, 'conditionalColumnHide.operator', '=');
      const hideIfValue = get(fieldConfig, 'conditionalColumnHide.value', null);
      const modelValue = get(this, hideIfFieldPath);
      if (this.compare(hideIfOperator, modelValue, hideIfValue)) {
        hide = true;
      }
    }

    const moduleName = get(fieldConfig, 'module', null);
    console.log('moduleName', moduleName);
    if (moduleName) {
      const activated = this.$modulesService.hasModuleActivated(moduleName);
      show = show && activated;
    }

    return !show || hide;
  }

  compare(operator, currentValue, targetValue) {
    switch (operator) {
      case '=':
        return currentValue == targetValue;
      case '!=':
        return currentValue != targetValue;
      case '>':
        return currentValue > targetValue;
      case '<':
        return currentValue < targetValue;
      case '>=':
        return currentValue >= targetValue;
      case '<=':
        return currentValue <= targetValue;
      default:
        return false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clickOnCheckAll() {
    this.$emit('click-on-check-all', !this.checkedAll);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clickOnCheckOne(row: any) {
    this.$emit('click-on-check-one', row);
  }

  clickOnAction(event, row) {
    this.$emit('click-on-action', { event: event, item: row });
  }

  // onPaginationChange(pagination: PaginationInterface) {
  //   console.log('onPaginationChange entity-table', pagination);
  //   this.$emit('pagination-change', pagination);
  // }

  getValue(item: any, field: string) {
    return get(item, field, get(item, this.camelize(field), ''));
  }

  /* Lifecycle hooks */
  // created () { }
  mounted() {
    this.view = this.initialView;
  }
  // updated () { }
  // destroyed () { }
  camelize(str) {
    return str.replace(/([-_]\w)/g, g => g[1].toUpperCase());
  }
  entitySaved() {
    this.$emit('entity-saved');
  }

  changeView(view) {
    this.view = view;
  }

  @Watch('initialView')
  onInitialViewChange() {
    this.view = this.initialView;
  }

  onClickDisplay($event) {
    this.$emit('on-click-display', $event);
  }
}
